/**
 * UI slice — everything in the prototype's `state` blob (INVENTORY §5.1) that
 * is view state rather than crew data, plus the actions that only move view
 * state around.
 *
 * Not ported: `capOv`, `chips`, `ptLastSync` (PORTING-NOTES D-06 — written but
 * never read). `intSkips` WAS in that list until 2026-08-24, when the design
 * gave the interests queue a SKIP FOR NOW worth persisting; it lives in the
 * boards slice with its GOTY twin, not here, because it is board state rather
 * than view state.
 */

import type { StateCreator } from 'zustand';

import { fieldXY, hexToOk } from '../../lib/color.ts';
import { norm } from '../../lib/format.ts';
import {
  defaultFilters,
  DISCOVER_LIMIT,
  GOTY_TODO_ROW,
  INT_TODO_ROW,
  intQueue,
  isCall,
  isTier,
  newForMe,
  snapPrice,
  tfToggle as tfToggleFn,
} from '../../lib/logic.ts';
import type {
  BoardKey,
  FilterKey,
  Filters,
  Game,
  GameId,
  Invite,
  PtCheckResult,
  SortMode,
  TriState,
  VoteValue,
} from '../../lib/types.ts';
import type {
  GotyCandidate,
  GotyLibraryGame,
  GotySignalCounts,
} from '../../repo/Repository.ts';
import { emptyBoardQueries, emptyFilterMap, fltKeyOf } from '../filterKey.ts';
import { linkStateOf, ownableKeys } from '../ownedBy.ts';
import type { QdStore } from '../types.ts';
import { feedMode } from './feed.ts';
import { withKnownFacts } from './steamFacts.ts';

/**
 * One line of copy for every way `set_player_count` can refuse, keyed by the
 * string it raises. Each names what happened rather than that something did:
 * four of the six are fixable by the member without leaving the editor, and a
 * generic failure would hide which.
 *
 * `BAD_NOTE` is absent on purpose — W5 sends no note, so it is unreachable, and
 * an entry for it would imply a field that does not exist. It falls to the
 * catch-all with `FAILED`.
 */
/**
 * The appids the OWNED BY filter has to be able to judge — everything a board
 * or a client-filtered Discover mode can put on screen.
 *
 * `catalog` covers the four boards, the Round shelf and Discover's local mode;
 * `live` covers a Steam browse, whose rows are filtered client-side too. The
 * FEED is deliberately absent: `discover_page` applies the same AND server-side
 * (migration 3047) because only it can keep `total` and `hidden_by_filters`
 * honest, so asking about those rows would buy a round trip and change nothing.
 *
 * Unfiltered by `browsable()` on purpose. Excluding provisional rows would save
 * a handful of ids off one request and cost the guarantee this list is for —
 * that anything the user can SEE has a real answer rather than the silence
 * `passFlt` reads as "do not hide it".
 */
function ownFilterAppids(s: QdStore): number[] {
  const out: number[] = [];
  for (const g of s.catalog) if (g.appid > 0) out.push(g.appid);
  for (const g of s.live) if (g.appid > 0) out.push(g.appid);
  return out;
}

const CAP_ERR: Readonly<Record<string, string>> = {
  NOT_AUTHENTICATED: '✕ SIGN IN AGAIN',
  MUST_RESET: '✕ SET A REAL PASSWORD FIRST',
  RATE_LIMITED: '✕ TOO MANY EDITS THIS HOUR — TRY LATER',
  UNKNOWN_GAME: "✕ THAT GAME ISN'T IN THE CATALOG YET",
  BAD_RANGE: "✕ THAT RANGE ISN'T VALID",
};

/**
 * How a MAKE YOUR CALLS run signs off. `ALL CALLS IN` is a claim about the
 * whole queue, and a skip defers a call rather than resolving it — so a run
 * that skipped six of seven ends with six still owed, and the Tonight nudge
 * behind the card says exactly that the moment it closes.
 */
const runEndToast = (skipped: number): string =>
  skipped ? skipped + ' STILL TO CALL' : 'ALL CALLS IN';

export type Zone = 'int' | 'goty';

export interface DragState {
  zone: Zone;
  row: string;
  id: GameId;
  name: string;
}

export interface DragOver {
  zone: Zone;
  row: string;
  idx: number;
}

export interface MoveSheet {
  kind: 'int' | 'goty';
  id: GameId;
}

export interface UiSlice {
  // discover ---------------------------------------------------------------
  q: string;
  sort: SortMode;
  view: 'g' | 'l';
  /**
   * How many Discover results are currently revealed — the window that replaced
   * the hard 60-cap. `discoverVM` clamps it to the true total, so a filter that
   * narrows the set never leaves a stale count behind.
   *
   * Reset by `setQ` / `clearQ` / `setSort` and NOT by the filter setters. A new
   * query or a new ordering is a new list and should start at the top; adjusting
   * a filter is refining the list you are already reading, where throwing away
   * the pages you loaded is the more annoying behaviour.
   */
  discoverShown: number;
  // tonight ----------------------------------------------------------------
  tFilter: string[];
  // detail -----------------------------------------------------------------
  draft: string;
  /**
   * The desktop FRIENDS panel, open or collapsed — and `null` for "nobody has
   * chosen yet on this game", which the SCREEN resolves against the breakpoint.
   *
   * The three states exist so the store never learns about viewport width. The
   * prototype re-seeds this in `openGame` as `dPanel: (S.w || 9999) >= 1080`
   * (`Games Tonight.dc.html:1934`), which works there because its state bag and
   * its window are the same object. Here the bag is shared with the phone tree,
   * where a desktop breakpoint has no meaning, so the width stays in
   * `useBreakpoint` and this field carries only the user's override of it.
   *
   * The `(w || 9999)` guard therefore does NOT port as an expression — and it
   * would invert if it did, because `readBreakpoint` answers `false` when it
   * cannot tell, where the prototype's `9999` answers wide. What ports is the
   * INVARIANT the guard protected: exactly one of panel and tab renders at all
   * times, which the screen gets by deriving both from `dPanel ?? wide` rather
   * than from two independent conditions.
   */
  dPanel: boolean | null;
  /**
   * The phone's full-screen FRIENDS view. The phone has no inline panel at any
   * width, so this is a separate surface rather than the same flag at a
   * different size — and it is registered in `history.ts` so the OS back gesture
   * closes it and the body scroll lock freezes the page under it.
   */
  friendsOpen: boolean;
  /**
   * The list REMOVE control, armed for its second press.
   *
   * A boolean where `rmArm`/`ptRmArm` are keyed strings, and the difference is
   * deliberate: those arm one row out of many and the key is what moves the arm
   * between rows, whereas detail has exactly one remove target on screen. What
   * makes the boolean safe is `openGame` clearing it — without that, arming on
   * one game would carry into the next.
   *
   * Removing takes the game off the list for the WHOLE CREW, its calls and
   * comments included, which is why it arms on both trees even though the phone
   * mirror fires on one press (`Games Tonight Mobile.dc.html:3088`). W.md C7
   * already ruled the same way on the desktop panel's REMOVE.
   */
  listRmArm: boolean;
  /**
   * The player-count editor: the appid it is editing, `0` when closed.
   *
   * An id rather than a boolean because the editor writes a GLOBAL value and
   * the write has to name the row it was opened on — a boolean would let a
   * navigation between opening and saving retarget the write at whatever
   * `detailId` had become.
   */
  capOpen: number;
  /**
   * The two inputs, as STRINGS. A number would make "cleared the field" and
   * "typed 0" the same state, and 0 is a value the RPC rejects — so the member
   * would get `MIN PLAYERS MUST BE AT LEAST 1` for an empty box.
   */
  capMn: string;
  capMx: string;
  /** The reset control, armed for its second press. */
  capArm: boolean;
  /**
   * The SET control, armed for its second press — but ONLY when an override
   * already exists.
   *
   * The two cases are not equally destructive and arming both would train the
   * confirmation away. A FIRST set is recoverable: RESET puts the row back on
   * whatever source rung it was on. Setting over an EXISTING override is not —
   * it replaces another member's correction for every crew at once, takes
   * attribution for the number, and the value it replaced survives only in
   * `game_players_audit`, which no member can read. So the second press is
   * asked for exactly where the first one cannot be undone.
   */
  capSetArm: boolean;
  /**
   * A player-count write is open — three sequential round trips (the RPC, the
   * attribution re-read, and the catalog patch between them), during which the
   * editor used to sit completely still.
   */
  capBusy: boolean;
  // interests / goty -------------------------------------------------------
  iMode: 'board' | 'list';
  gq: string;
  // filters ----------------------------------------------------------------
  /**
   * ONE SET PER SCREEN, never one object shared five ways — `store/filterKey.ts`
   * has the argument and the two ways to read it. Nothing outside the shared
   * panel may reach for "the current screen's": a data selector names its key.
   */
  fltBy: Record<FilterKey, Filters>;
  /**
   * THE BOARD SEARCH TERM, one per board (DESIGN-CHANGELOG 2026-08-28).
   *
   * The design says "one query, the way there is one filter state" — and in the
   * PROTOTYPE there is one filter state, a single `flt`. Here there is not:
   * Marcus split filters per screen on 2026-08-27 ("I don't want a universal app
   * filter, I want per screen filters") precisely because a narrowing that
   * followed you between screens read as data loss rather than as a filter. A
   * shared query has that failure mode in its sharpest form — type `hades` on
   * TONIGHT, tab to GOTY, and GOTY is silently down to one row with the box
   * that explains it two screens back.
   *
   * So what ports is the design's ANALOGY rather than its literal noun: the
   * query travels exactly as far as the filter does, which here is not at all.
   * Same shape as `fltBy`, same key discipline (`store/filterKey.ts`) — a
   * selector names its board, only the panel may ask "the current screen's".
   *
   * Typed by `BoardKey`, not `FilterKey`, so Discover's separate `q` cannot be
   * written here by accident.
   */
  bqBy: Record<BoardKey, string>;
  fltOpen: boolean;
  fltPrDrag: 'lo' | 'hi' | '';
  // move sheet + drag ------------------------------------------------------
  sheet: MoveSheet | null;
  drag: DragState | null;
  dragOver: DragOver | null;
  // what's new -------------------------------------------------------------
  wnOpen: boolean;
  wnIdx: number;
  wnQueue: GameId[];
  /**
   * Which run the card is serving (DESIGN-CHANGELOG 2026-08-24).
   *
   *   `new`    unread news — the header bell's queue. Marking a card reviews
   *            it, and CLEAR ALL is offered.
   *   `calls`  the MY INTERESTS chore list, opened from the Tonight nudge.
   *            Nothing here is unread news, so nothing gets marked reviewed
   *            and CLEAR ALL is hidden.
   *
   * The two modes drive the same card but different WRITES — see `wnSkip`,
   * where they are genuinely different actions that happen to share a button.
   */
  wnMode: 'new' | 'calls';
  /** How many cards THIS run deferred — what the closing toast reports. */
  wnSkipped: number;
  // admin forms ------------------------------------------------------------
  addName: string;
  addEmail: string;
  invite: Invite | null;
  rmArm: string;
  // profile forms ----------------------------------------------------------
  pfName: string;
  pfEmail: string;
  /** The address this account signs in with, as loaded. `pfEmail` is the
   *  edit-in-progress; this is the committed truth the form compares against. */
  pfAuthEmail: string;
  pfCur: string;
  pfN1: string;
  pfN2: string;
  /** The phone CHANGE PASSWORD sheet (`design/GT Mobile Crew.dc.html`;
   *  DESIGN-CHANGELOG 2026-08-18). Desktop keeps its inline SECURITY panel and
   *  never reads this. Registered in `store/history.ts`. */
  pwOpen: boolean;
  /**
   * The CHANGE EMAIL sheet, and its own two fields.
   *
   * DELIBERATELY NOT `pfCur`. The password sheet clears `pfCur` on both open
   * and close, so an email form sharing it would find the field emptied out
   * from under it the moment either sheet was touched. Two controls that both
   * ask for the current password need two places to put it.
   */
  emOpen: boolean;
  /**
   * The crew switcher — a dropdown on desktop, a bottom sheet on the phone.
   * One flag for both, because only one column is mounted at a time.
   */
  swOpen: boolean;
  /**
   * The desktop header's ACCOUNT MENU (DESIGN-CHANGELOG 2026-09-03 "Account
   * chip"): role · party, the STEAM line, EDIT PROFILE and SIGN OUT hang off
   * the avatar chip, which is a menu now and not a link. Desktop-only — the
   * phone's avatar opens the profile screen, which already carries both — but
   * the flag lives here beside `swOpen` because it is the same kind of thing:
   * a dropdown that any navigation closes.
   */
  meOpen: boolean;
  /**
   * OWNER CONTROLS' two destructive confirmations. One field rather than two
   * booleans: they are mutually exclusive, and a single value cannot get into
   * the state where both are open.
   */
  ownModal: '' | 'transfer' | 'delete';
  /** DELETE CREW's typed-name gate. */
  delTxt: string;
  /** TRANSFER OWNERSHIP's chosen member, by userKey. */
  trPick: string;
  /** The new login address being typed. */
  emNew: string;
  /** Current password, for the email sheet's own gate. */
  emCur: string;
  // custom color picker (CHOOSE YOUR COLOR, migration 3012) ----------------
  /** the picker overlay — desktop modal / phone sheet; registered in history */
  ccOpen: boolean;
  /** crosshair x ∈ [0,1] along the arc-length hue axis */
  ccU: number;
  /** crosshair y ∈ [0,1], pale top → deep bottom */
  ccV: number;
  /** a point is armed — `USE THIS COLOR ▸` commits only when true */
  ccSel: boolean;
  // playtests UI -----------------------------------------------------------
  ptQ: string;
  ptChips: string[];
  ptShowEnded: boolean;
  ptDet: string;
  ptAddOpen: boolean;
  ptAddTab: 'steam' | 'off';
  ptAddUrl: string;
  ptAddChk: PtCheckResult | null;
  /** P6 F-2's round trip is in flight; the P3e-03 busy flag for the CHECK button. */
  ptAddChecking: boolean;
  /**
   * The TRACK write is in flight — the sibling CHECK has had a flag since
   * P3e-03 and this one never got one.
   *
   * It matters more than CHECK's did. The off-Steam arm inserts `appid: null`
   * with no slug, and BOTH unique indexes on `playtests` are partial —
   * `playtests_crew_appid` is `where appid is not null and appid > 0`,
   * `playtests_crew_slug` is `where slug is not null` — so nothing in the
   * database stops the second row. Two presses tracked the same playtest twice
   * for the whole crew, twice in What's New.
   *
   * Shared by both tabs: one add panel, one write at a time.
   */
  ptTracking: boolean;
  ptAddParty: number;
  ptOffName: string;
  ptOffUrl: string;
  ptOffStart: string;
  ptOffEnd: string;
  ptBrowseOpen: boolean;
  ptBq: string;
  ptTrackedOpen: boolean;
  /**
   * The id of the *non-auto* playtest armed for untrack — the Crew REMOVE
   * `rmArm` pattern applied to the two destructive untrack controls: the
   * tracked sheet's `✕` and the detail sheet's UNTRACK.
   *
   * PORTING-NOTES D-04 splits both in two: an `auto` playtest is muted for me
   * only (safe, stays single-tap), anything else is removed crew-wide with its
   * cascading rows. A stray tap on the second kind deleted a seeded playtest
   * for the whole crew (docs/porting/P4b.md §7), so it now arms first and acts
   * on the second tap. One string, like `rmArm`: arming another row moves the
   * arm rather than accumulating one per row, and the two sheets share it
   * because they are never both interactive.
   */
  ptRmArm: string;
  /**
   * The playtest whose “DID YOU REQUEST ACCESS?” confirm is open; `''` = closed.
   *
   * Opened by the primary CTA (`REQUEST ACCESS ↗` / `SIGN UP ↗`) at the same
   * moment it hands off to the store page, because that hand-off is the ONLY
   * moment the app can know a request was even attempted — Steam publishes
   * ownership, never a pending request, so `steam-sync` can detect
   * “got in” and can never detect “requested” (functions/steam-sync/index.ts
   * writes `status:'in'` and nothing else). The alternative was assuming the
   * click IS a request, which would put members in the REQUESTED list for
   * tabs they closed without asking.
   *
   * A single string, like `ptDet`: the confirm is modal over one card and two
   * of them are never open at once.
   */
  ptAsk: string;
  ptSyncOpen: boolean;
  /**
   * A Steam link hand-off or unlink is open.
   *
   * The link button is the one place in the app where a second press has a
   * consequence outside it: each call opens a Steam sign-in tab, and when the
   * popup blocker refuses the second the fallback navigates the whole app to
   * Steam instead.
   */
  steamBusy: boolean;

  /**
   * The GOTY auto-fill picker (G-07).
   *
   * Opened by `steamFill` when Steam returns a library it refuses to date, which
   * for a crew sharing one Web API key is every member except the key's owner.
   * The primary rule is then unsatisfiable rather than merely empty, so instead
   * of writing a board the auto-fill hands over what its fallback signals could
   * recover and lets the member say which of it is real.
   *
   * The payload lives here rather than being refetched because the round trip
   * that produced it is expensive — one keyed library call plus up to eighty
   * achievement calls — and reopening the sheet must not spend it again.
   */
  gotyPickOpen: boolean;
  /** Ranked confident-first by the server; the order the picker shows. */
  gotyPickCands: GotyCandidate[];
  /** The member's whole owned library, for the "add something we missed" search. */
  gotyPickLib: GotyLibraryGame[];
  /** Null when the fallback did not run. Drives the explanation copy. */
  gotyPickCounts: GotySignalCounts | null;
  /**
   * Ticked appids. A plain array, not a Set: zustand compares by reference and
   * an in-place Set mutation would not re-render — the same reason `ptChips`,
   * `tFilter` and the tag lists are all arrays.
   */
  gotyPickOn: number[];
  gotyPickQ: string;

  /** forces the 5 s "ago" re-render, prototype `componentDidMount` line 1298 */
  tick: number;

  // actions ----------------------------------------------------------------
  /** `onQ` — 380 ms debounce, ≥2 chars (prototype line 1618). */
  setQ: (q: string) => void;
  clearQ: () => void;
  /** `fetchLiveCS` — the CheapShark fallback of INVENTORY §6.2 (P3c-02). */
  searchLiveCS: (q: string) => Promise<void>;
  setSort: (v: SortMode) => void;
  setView: (v: 'g' | 'l') => void;
  /** Reveal one more page of Discover results (docs/DISCOVERY.md §4 item 2). */
  loadMoreDiscover: () => void;
  setIMode: (v: 'board' | 'list') => void;
  setGq: (v: string) => void;
  setDraft: (v: string) => void;
  /**
   * Flip the desktop FRIENDS panel. `wide` is the caller's breakpoint answer,
   * passed in rather than read here so the store stays viewport-blind — it is
   * only used to resolve the `null` "not chosen yet" state into the boolean
   * being flipped, so the first click always does the visible thing.
   */
  togglePanel: (wide: boolean) => void;
  openFriends: () => void;
  closeFriends: () => void;
  /** Arm or disarm the crew-wide list REMOVE (see `listRmArm`). */
  setListRmArm: (v: boolean) => void;
  /** Open the player-count editor on one game, inputs seeded from its count. */
  openCap: (appid: number) => void;
  closeCap: () => void;
  setCap: (patch: { mn?: string; mx?: string }) => void;
  setCapArm: (v: boolean) => void;
  setCapSetArm: (v: boolean) => void;
  /** Write the typed range for EVERYONE. Validates before the round trip. */
  saveCap: () => Promise<void>;
  /** Drop the override back to whatever the sources say. */
  clearCap: () => Promise<void>;

  /**
   * Set one board's search term. NAMES ITS BOARD — there is no "current
   * screen's" reader for `bqBy`, because unlike the filter panel every box is
   * mounted inside the board it narrows and so always knows its own key.
   */
  setBq: (key: BoardKey, v: string) => void;

  setFlt: (patch: Partial<Filters>) => void;
  /**
   * CLEAR ALL — every filter AND the picked tags, from wherever it is pressed.
   *
   * Unscoped since every filter applies on every surface (2026-08-27): the old
   * lens branch cleared only the crew lens, which would now leave a board
   * narrowed by the price cap the button just claimed to clear. The tags go
   * with them because they sit in the same panel under the same button.
   */
  clearFlt: () => void;
  toggleFltOpen: (open?: boolean) => void;
  setFltPrDrag: (v: 'lo' | 'hi' | '') => void;
  setPrLo: (v: number) => void;
  setPrHi: (v: number) => void;
  setStatusFilter: (k: keyof Filters['st'], v: TriState) => void;
  /**
   * OWNED BY — add or remove one member from the current screen's set.
   *
   * REFUSES A MEMBER WHO HAS NOT LINKED STEAM, and the refusal lives here
   * rather than only in the panel's disabled state. An unlinked member owns
   * nothing we can see, so putting their key in `Filters.own` empties every
   * board — a filter that can only ever return zero rows is a trap, and the
   * store is the layer that can promise it never happens whatever the two
   * panels do.
   */
  toggleOwn: (k: string) => void;
  /**
   * The `ALL` shortcut: every LINKED member on, or — when they already all are
   * — everything off. One control, because "the whole crew owns it" is the
   * question this section exists to ask and nobody wants to click six names to
   * ask it.
   *
   * Unlinked members are not in the on-state, so pressing ALL twice is a clean
   * round trip rather than a set that quietly loses members.
   */
  toggleOwnAll: () => void;

  tfToggle: (k: string) => void;
  tfAll: () => void;

  openSheet: (kind: 'int' | 'goty', id: GameId) => void;
  closeSheet: () => void;

  // what's new — prototype 1843-1871 -----------------------------------------
  openWhatsNew: () => void;
  /**
   * Run the same card over every game I have not called, in MY INTERESTS queue
   * order (DESIGN-CHANGELOG 2026-08-24). The Tonight nudge passes the ids it
   * counted, so the number on the button and the length of the run cannot
   * disagree; with none it recomputes.
   */
  openCallRun: (ids?: GameId[]) => void;
  closeWhatsNew: () => void;
  /** vote the current card and advance; the last one closes with a toast */
  wnMark: (l: VoteValue) => Promise<void>;
  /** dismiss the current card and advance — what that WRITES depends on the mode */
  wnSkip: () => Promise<void>;
  /** `markReviewed` + mark every queued id seen, then close with `ALL CLEARED` */
  wnClearAll: () => Promise<void>;

  dragStart: (d: DragState) => void;
  setDragOver: (ov: DragOver | null) => void;
  /** commits the drop through the boards/list data ops, then clears */
  dragEnd: () => void;
  dragCancel: () => void;

  setProfileForm: (
    patch: Partial<Pick<UiSlice, 'pfName' | 'pfEmail' | 'pfCur' | 'pfN1' | 'pfN2' | 'emNew' | 'emCur'>>,
  ) => void;
  openPw: () => void;
  closePw: () => void;
  openEm: () => void;
  closeEm: () => void;
  openSw: () => void;
  closeSw: () => void;
  toggleMe: () => void;
  closeMe: () => void;
  openOwn: (which: 'transfer' | 'delete') => void;
  closeOwn: () => void;
  setOwnForm: (patch: Partial<Pick<UiSlice, 'delTxt' | 'trPick'>>) => void;
  openCc: () => void;
  closeCc: () => void;
  setCcPoint: (u: number, v: number) => void;
  setAdminForm: (patch: Partial<Pick<UiSlice, 'addName' | 'addEmail' | 'rmArm'>>) => void;
  closeInvite: () => void;

  setPtQ: (v: string) => void;
  togglePtChip: (id: string) => void;
  togglePtEnded: () => void;
  openPtDet: (id: string) => void;
  closePtDet: () => void;
  openPtAdd: () => void;
  closePtAdd: () => void;
  setPtAddTab: (t: 'steam' | 'off') => void;
  setPtAddUrl: (v: string) => void;
  setPtAddParty: (n: number) => void;
  setPtOff: (patch: Partial<Pick<UiSlice, 'ptOffName' | 'ptOffUrl' | 'ptOffStart' | 'ptOffEnd'>>) => void;
  openPtBrowse: () => void;
  closePtBrowse: () => void;
  setPtBq: (v: string) => void;
  openPtTracked: () => void;
  closePtTracked: () => void;
  /** arms `id` for the crew-wide untrack (tracked `✕` / detail UNTRACK); `''` disarms (see `ptRmArm`). */
  setPtRmArm: (id: string) => void;
  /** open the “did you request access?” confirm for `id` (see `ptAsk`) */
  openPtAsk: (id: string) => void;
  closePtAsk: () => void;
  openPtSync: () => void;
  closePtSync: () => void;

  /** Hand the auto-fill's fallback result to the picker (G-07). `ticked` is the
   *  pre-selection — the server's confident candidates, never its guesses. */
  openGotyPick: (p: {
    candidates: GotyCandidate[];
    library: GotyLibraryGame[];
    counts: GotySignalCounts | null;
    ticked: number[];
  }) => void;
  closeGotyPick: () => void;
  toggleGotyPick: (appid: number) => void;
  setGotyPickQ: (v: string) => void;

  bumpTick: () => void;
}

export const SEARCH_DEBOUNCE_MS = 380;

// ---------------------------------------------------------------------------
// CheapShark fallback (INVENTORY §6.2, prototype `fetchLiveCS` line 1643)
// ---------------------------------------------------------------------------

/**
 * P6 F-1: this is no longer a direct third-party call.
 *
 * It used to be the *only* origin other than our own that the browser touched —
 * `https://www.cheapshark.com/api/1.0/games?title=…&limit=20`, kept verbatim
 * from the prototype because CheapShark sends its own CORS headers and there was
 * no Worker route (PORTING-NOTES P2a-09, docs/porting/P3c.md P3c-02). Phase 6
 * shipped `GET /api/cs/search?q=`, so the call now goes through
 * `repo.fetchCsRows` like every other upstream: one origin, the §4.3 envelope, a
 * 60 s shared edge cache, and no visitor IP handed to CheapShark. `limit=20`
 * moved server-side (`worker/src/cheapshark.ts` `CS_LIMIT`); `LIVE_LIMIT` below
 * is the client's own slice and is unchanged.
 */
/** The prototype had no abort for this call; 4 s matches its Steam timeout. */
export const CHEAPSHARK_TIMEOUT_MS = 4000;
/** Prototype `.slice(0, 12)`. */
export const LIVE_LIMIT = 12;

const CS_DESC =
  'Live search result via the CheapShark index. Add it to the list and friends can fill in details.';

interface CsRow {
  gameID?: string | number;
  steamAppID?: string | number | null;
  external?: string;
  cheapest?: string | number;
  thumb?: string;
}

/**
 * One CheapShark row → the prototype's live-game shape (line 1645). Rows with
 * no numeric `steamAppID` keep the minted `cs<gameID>` id and an `appid` of 0 —
 * PORTING-NOTES D-02 makes those display-only (quick-add disabled).
 */
export function toCsGame(row: unknown): Game | null {
  const r = (row && typeof row === 'object' ? row : {}) as CsRow;
  const n = typeof r.external === 'string' ? r.external : '';
  if (!n) return null;
  const aid = Number(r.steamAppID) || 0;
  const gid = r.gameID === undefined || r.gameID === null ? '' : String(r.gameID);
  if (!aid && !gid) return null;
  const price = Number(r.cheapest);
  return {
    id: aid || 'cs' + gid,
    appid: aid,
    n,
    dev: '—',
    rd: '',
    p: Number.isFinite(price) ? price : null,
    o: 0,
    mn: 0,
    mx: 0,
    plat: 'P',
    tags: [],
    rp: 0,
    pk: 0,
    ccu: 0,
    up: false,
    ds: CS_DESC,
    ea: false,
    demo: false,
    dAid: 0,
    art: '',
    live: true,
    thumb: typeof r.thumb === 'string' ? r.thumb : '',
  };
}

/**
 * Raw CheapShark payload → at most 12 live rows, dropping anything the catalog
 * already holds by appid or by normalized name (prototype line 1644).
 */
export function mapCheapShark(rows: unknown, catalog: readonly Game[]): Game[] {
  if (!Array.isArray(rows)) return [];
  const ids = new Set(catalog.map((g) => g.id));
  const norms = new Set(catalog.map((g) => norm(g.n)));
  const out: Game[] = [];
  for (const row of rows) {
    const g = toCsGame(row);
    if (!g) continue;
    if (g.appid && ids.has(g.appid)) continue;
    if (norms.has(norm(g.n))) continue;
    out.push(g);
    if (out.length === LIVE_LIMIT) break;
  }
  return out;
}

export const createUiSlice: StateCreator<QdStore, [], [], UiSlice> = (set, get) => {
  /** Search debounce, prototype `this._deb`. Per store, not per module. */
  let debT: ReturnType<typeof setTimeout> | null = null;

  /**
   * The write, shared by SET and CLEAR because only the arguments differ.
   *
   * Deliberately NOT routed through `mutate()`: its `SNAPSHOT_KEYS` has no games
   * collection, so a Game patch could not be rolled back, and `RATE_LIMITED`
   * would land in `NETWORK_CODES` and be silently retried — against a cap that
   * guarantees the retry fails too.
   *
   * The catalog is patched from what the RESOLVER returned, never from what was
   * typed. For a SET those agree, because an override is the top rung. For a
   * CLEAR they cannot: the rung underneath was never sent to this client, so
   * predicting it would leave the rail showing the number just deleted.
   */
  const applyCap = async (
    appid: number,
    mn: number | null,
    mx: number | null,
    okMsg: string,
  ): Promise<void> => {
    const s = get();
    // Clears at the START of the write, not at its end. `capArm` used to be
    // reset only in the success block, so a failed CLEAR left the control still
    // armed — the next single press went straight through to a second attempt.
    set({ capBusy: true, capArm: false, capSetArm: false });
    try {
      const res = await s.repo.setPlayerCount(appid, mn, mx);
      if (res) {
        const patch = (g: Game): Game => {
          if (g.appid !== appid) return g;
          const next = { ...g, mn: res.mn, mx: res.mx };
          // Assigned rather than spread as `undefined`: absence is the
          // app-wide encoding for "no flag" / "nothing resolved", and an
          // explicit `undefined` key is a third state nothing else writes.
          if (res.punb) next.punb = true;
          else delete next.punb;
          if (res.psrc) next.psrc = res.psrc;
          else delete next.psrc;
          return next;
        };
        // ALL THREE populations `gById` answers a Detail open from — `catalog`
        // (tracked / curated rows), `live` (a Discover search or a pool page)
        // and `feedRows` — exactly as `applyEnrich` patches all three, and for
        // the same reason: a row that is not in `catalog` is not a lesser row,
        // it is the same appid reached by a different door.
        //
        // Patching only `catalog` is why setting a count on a pool row LOOKED
        // like it did nothing: the write, the resolver and the server read were
        // all correct (verified on staging against The Jackbox Party Pack 10,
        // appid 2216830 — `ovr_min/ovr_max` 1/6, `v_games` agreeing), the toast
        // said PLAYER COUNT SET FOR EVERYONE, and the cell kept rendering the
        // pre-edit value until a reload. A success message over an unchanged
        // screen is worse than an error: it teaches people the feature is
        // broken in a way they cannot describe.
        set((st) => ({
          catalog: st.catalog.map(patch),
          live: st.live.map(patch),
          feedRows: st.feedRows.map(patch),
        }));
      }
      // Re-read the attribution rather than minting it locally — the reader
      // decides what this member is allowed to see about the setter, and after
      // a CLEAR there is nothing to see at all.
      let o = null;
      try {
        o = await s.repo.fetchPlayerOverride(appid);
      } catch {
        o = null;
      }
      s.setPlayerOverrideCache(appid, o);
      set({ capOpen: 0, capMn: '', capMx: '', capArm: false, capSetArm: false });
      s.toast(okMsg);
    } catch (e) {
      const code = e instanceof Error ? e.message : '';
      // The editor STAYS OPEN on failure. Every one of these is either
      // retryable or fixable in place, and closing would throw away what was
      // typed to tell somebody their session expired.
      s.toast(CAP_ERR[code] ?? "✕ COULDN'T SAVE — TRY AGAIN", true);
    } finally {
      set({ capBusy: false });
    }
  };

  return {
  q: '',
  sort: 'rel',
  view: 'g',
  discoverShown: DISCOVER_LIMIT,
  tFilter: [],
  draft: '',
  dPanel: null,
  friendsOpen: false,
  listRmArm: false,
  capOpen: 0,
  capMn: '',
  capMx: '',
  capArm: false, capSetArm: false,
  capBusy: false,
  iMode: 'board',
  gq: '',
  fltBy: emptyFilterMap(),
  bqBy: emptyBoardQueries(),
  fltOpen: false,
  fltPrDrag: '',
  sheet: null,
  drag: null,
  dragOver: null,
  wnOpen: false,
  wnIdx: 0,
  wnQueue: [],
  wnMode: 'new',
  wnSkipped: 0,
  addName: '',
  addEmail: '',
  invite: null,
  rmArm: '',
  pfName: '',
  pfEmail: '',
  pfAuthEmail: '',
  pfCur: '',
  pfN1: '',
  pfN2: '',
  pwOpen: false,
  emOpen: false,
  swOpen: false,
  meOpen: false,
  ownModal: '' as '' | 'transfer' | 'delete',
  delTxt: '',
  trPick: '',
  emNew: '',
  emCur: '',
  ccOpen: false,
  ccU: 0.5,
  ccV: 0.5,
  ccSel: false,
  ptQ: '',
  ptChips: [],
  ptShowEnded: false,
  ptDet: '',
  ptAddOpen: false,
  ptAddTab: 'steam',
  ptAddUrl: '',
  ptAddChk: null,
  ptAddChecking: false,
  ptTracking: false,
  ptAddParty: 0,
  ptOffName: '',
  ptOffUrl: '',
  ptOffStart: '',
  ptOffEnd: '',
  ptBrowseOpen: false,
  ptBq: '',
  ptTrackedOpen: false,
  ptRmArm: '',
  ptAsk: '',
  ptSyncOpen: false,
  steamBusy: false,
  gotyPickOpen: false,
  gotyPickCands: [],
  gotyPickLib: [],
  gotyPickCounts: null,
  gotyPickOn: [],
  gotyPickQ: '',
  tick: 0,

  /**
   * Discover's search box now drives BROWSE, not the 10-row typeahead.
   *
   * `storesearch` caps at 10 results and cannot page (docs/DISCOVERY.md §2.2), so
   * it could never answer "show me co-op horror games" — it was only ever a
   * jump-to-this-title lookup. `fetchBrowse` reaches Steam's real search: ~272k
   * games, 50 a page, and combinable with the tag picker.
   *
   * The debounce, the reveal-window reset and the below-minimum clear all behave
   * as before; only the upstream changed. `searchLive`/`searchLiveCS` stay on
   * the repository — they are still the CheapShark-backed path and still under
   * test — but nothing on Discover calls them now.
   */
  setQ: (q) => {
    set({ q, discoverShown: DISCOVER_LIMIT });
    if (debT) clearTimeout(debT);
    // Below the minimum with no tag picked, there is no question to ask. With a
    // tag picked there is, so the browse still runs — a one-character narrowing
    // inside Co-op is legitimate, and the Worker allows exactly that case.
    if (q.trim().length < 2 && get().bTagsBy.discover.length === 0) {
      set({ live: [], liveState: '', bTotal: 0, bStart: 0, bCapped: false, bState: '', bLoaded: 0 });
      // Backspacing out of a search returns to the DEFAULT view, which is a
      // server-ranked feed — including the single-character case, which is
      // below the browse threshold but is still not a question.
      if (q.trim() === '') get().restartFeed();
      return;
    }
    set({ liveState: 'busy', bState: 'busy', live: [], bTotal: 0, bStart: 0, bCapped: false, bLoaded: 0 });
    debT = setTimeout(() => {
      void get().fetchBrowse();
    }, SEARCH_DEBOUNCE_MS);
  },
  clearQ: () => {
    if (debT) clearTimeout(debT);
    set({
      q: '',
      live: [],
      liveState: '',
      discoverShown: DISCOVER_LIMIT,
      bTotal: 0,
      bStart: 0,
      bCapped: false,
      bState: '',
      bLoaded: 0,
    });
    // Clearing the box does not clear a picked tag — the tag is its own visible
    // control with its own ✕, and silently dropping it would be the screen
    // changing a filter the user can still see.
    if (get().bTagsBy.discover.length > 0) void get().fetchBrowse();
    // With no tag left there is no question, so the default view comes back.
    else get().restartFeed();
  },

  searchLiveCS: async (q) => {
    set({ liveState: 'busy' });
    const ctl = typeof AbortController === 'undefined' ? null : new AbortController();
    const t = ctl ? setTimeout(() => ctl.abort(), CHEAPSHARK_TIMEOUT_MS) : null;
    let rows: unknown[] = [];
    try {
      // The rows arrive at `body.data.rows`; the repository unwraps the §4.3
      // envelope and throws on `ok:false` (and on `stale`, which §4.3 counts as
      // a failure), which is the `liveState:'err'` path — same shape as the raw
      // `fetch` rejection this replaced.
      rows = await get().repo.fetchCsRows(q, ctl ? ctl.signal : undefined);
    } catch {
      if (t) clearTimeout(t);
      if (get().q.trim() !== q) return;
      set({ live: [], liveState: 'err' });
      return;
    }
    if (t) clearTimeout(t);
    if (get().q.trim() !== q) return;
    const st = get();
    // Same hole as the Steam path: `toCsGame` cannot know a release state, so a
    // CheapShark row arrives flagless and would render without its ribbon.
    // Rows with no Steam appid (`cs<gameID>`) pass through untouched.
    const live = mapCheapShark(rows, st.catalog).map((g) => withKnownFacts(g, st.catalog, st.facts));
    set({ live, liveState: 'ok', liveSrc: 'cs' });
  },
  setSort: (sort) => {
    const prev = get();
    set({ sort, discoverShown: DISCOVER_LIMIT });
    // BOTH pipelines sort SERVER-side, so a new sort is a new question and the
    // page in hand — feed cursor or browse offset — no longer means anything.
    // `restartDiscover` picks the live one; calling `restartFeed` directly is
    // what left the sort control inert for the whole of browse mode, since it
    // returns immediately when browse owns the screen.
    get().restartDiscover(prev);
  },
  setView: (view) => set({ view }),
  /**
   * Two different "more", because the two modes page differently and the button
   * is deliberately identical (§4 item 2).
   *
   * In the feed, LOAD MORE is a network round trip for the next cursor page. In
   * browse and in the Phase 1 fallback it raises a client-side reveal window
   * over rows already held. `discoverShown` is still bumped in both, so a
   * fallback that happens mid-session keeps a window big enough to show what it
   * already fetched.
   */
  loadMoreDiscover: () => {
    set((s) => ({ discoverShown: s.discoverShown + DISCOVER_LIMIT }));
    if (feedMode(get())) void get().fetchFeed({ more: true });
  },
  setIMode: (iMode) => set({ iMode }),
  setGq: (gq) => set({ gq }),
  setDraft: (draft) => set({ draft }),
  togglePanel: (wide) => set((s) => ({ dPanel: !(s.dPanel ?? wide) })),
  openFriends: () => set({ friendsOpen: true }),
  closeFriends: () => set({ friendsOpen: false }),
  setListRmArm: (listRmArm) => set({ listRmArm }),

  openCap: (appid) => {
    const g = get().gById(appid);
    // SEEDING HAS TO CLAMP, or the untouched form fails its own pre-flight.
    // `res_max` deliberately stores the winning raw value UNCLAMPED — IGDB
    // reports Factorio at 65535 — and `mn` is 0 on every unresolved row, so
    // copying them straight in opens an editor whose first press is an error
    // about numbers the member never typed. A sentinel gets blank boxes too:
    // there is no count under it to correct.
    const raw = g && !g.punb ? g.mx : 0;
    const ok = raw >= 1 && raw <= 1000;
    set({
      capOpen: appid,
      capMx: ok ? String(raw) : '',
      capMn: ok ? String(Math.max(1, g?.mn ?? 1)) : '',
      capArm: false, capSetArm: false,
    });
  },
  closeCap: () => set({ capOpen: 0, capMn: '', capMx: '', capArm: false, capSetArm: false }),
  // Typing DISARMS. The confirmation was about a specific number; changing the
  // number makes it a confirmation of something the member never saw.
  setCap: (patch) =>
    set((st) => ({
      capMn: patch.mn ?? st.capMn,
      capMx: patch.mx ?? st.capMx,
      capSetArm: false,
    })),
  setCapArm: (capArm) => set({ capArm }),
  setCapSetArm: (capSetArm) => set({ capSetArm }),

  saveCap: async () => {
    const s = get();
    const appid = s.capOpen;
    if (!appid) return;
    if (s.capBusy) return;
    // Pre-flight in the RPC's own order, so the message names the first thing
    // wrong rather than whichever the server happened to reach.
    const mx = Number(s.capMx.trim());
    const mn = Number(s.capMn.trim());
    if (!s.capMx.trim() || !Number.isFinite(mx) || mx === 0) {
      s.toast('MAX PLAYERS REQUIRED', true);
      return;
    }
    if (!Number.isInteger(mx) || mx < 1 || mx > 1000) {
      s.toast('MAX PLAYERS MUST BE 1-1000', true);
      return;
    }
    if (!Number.isInteger(mn) || mn < 1) {
      s.toast('MIN PLAYERS MUST BE AT LEAST 1', true);
      return;
    }
    if (mn > mx) {
      s.toast("MIN CAN'T BE MORE THAN MAX", true);
      return;
    }
    const g = s.gById(appid);
    // The RPC treats a restatement as a no-op and writes no audit row, so it
    // would return success having changed nothing. Saying so is better than a
    // confirmation for an edit that did not happen.
    if (g && !g.punb && g.mn === mn && g.mx === mx && g.psrc === 'override') {
      s.toast('MATCHES THE CURRENT COUNT — NOTHING TO CHANGE');
      return;
    }
    // Arms only where the press cannot be taken back. Replacing an EXISTING
    // override drops a number nobody in the app can recover — it survives only
    // in `game_players_audit`, which no member can read. A first set is not in
    // that class: RESET restores the source count exactly, so asking twice
    // there would be the confirmation that teaches people to click through.
    if (get().ovr[appid] && !get().capSetArm) {
      set({ capSetArm: true });
      return;
    }
    await applyCap(appid, mn, mx, 'PLAYER COUNT SET FOR EVERYONE');
  },

  clearCap: async () => {
    const s = get();
    const appid = s.capOpen;
    if (!appid) return;
    if (s.capBusy) return;
    await applyCap(appid, null, null, 'BACK TO THE SOURCE COUNT');
  },

  // EVERY filter mutator restarts DISCOVER — whichever of its pipelines is
  // live — because both of them now filter SERVER-side, and a stale page under
  // a new filter set would make `total` and `hiddenByFlt` describe a set the
  // rows did not come from: the §5.5 failure, arriving through a race rather
  // than a design mistake. These all called `restartFeed` directly, which
  // returns early in browse mode, so with a tag active a filter change reached
  // no pipeline at all. Both restarters debounce, which is what lets a price
  // slider call this on every pixel without anyone reasoning about burst rates.
  /**
   * Every setter below writes to the CURRENT screen's set and then dispatches
   * only if that screen is Discover — the pipelines are Discover's alone, and
   * off it a filter change is a selector recompute and nothing more.
   */
  /**
   * No debounce and no dispatch, unlike `setQ`: this term never leaves the
   * client. It narrows rows already in the store, so a keystroke is a selector
   * recompute — the same cost a filter change off Discover has.
   */
  setBq: (key, v) => set((s) => ({ bqBy: { ...s.bqBy, [key]: v } })),

  setFlt: (patch) => {
    const prev = get();
    const key = fltKeyOf(prev);
    set((s) => ({ fltBy: { ...s.fltBy, [key]: { ...s.fltBy[key], ...patch } } }));
    if (key === 'discover') get().restartDiscover(prev);
  },
  clearFlt: () => {
    const prev = get();
    const key = fltKeyOf(prev);
    set((s) => ({ fltBy: { ...s.fltBy, [key]: defaultFilters() } }));
    // `clearTags` runs its own restart (it has to decide between the browse and
    // feed pipelines off whether an INCLUDE went away), so let it, and only
    // dispatch here when there were no tags for it to clear.
    if (prev.bTagsBy[key].length > 0 || prev.xTagsBy[key].length > 0) get().clearTags();
    else if (key === 'discover') get().restartDiscover(prev);
  },
  toggleFltOpen: (open) => {
    const next = open ?? !get().fltOpen;
    set({ fltOpen: next, tagQ: '' });
    // The tag picker is a section of this panel now, so its 446-row vocabulary
    // is fetched on the panel's first open rather than on a trigger of its own.
    // Still not at boot: nobody needs it until they reach for the panel, and it
    // is cached for a day upstream so the second open is free.
    if (next) void get().loadTagVocab();
    if (next) {
      // OWNED BY needs two different reads and they answer two different
      // questions. WHO CAN BE PICKED comes from `crewLinks` — without it every
      // member renders PENDING and the section is inert, which is why this is a
      // panel-open concern and not a click-time one. WHAT THEY OWN comes from
      // `ownBy`, fetched here rather than on the first toggle so the match
      // count under the header is right the moment a name is pressed instead of
      // a round trip later.
      get().ensureCrewLinks();
      get().ensureOwnedBy(ownFilterAppids(get()));
    }
  },
  setFltPrDrag: (fltPrDrag) => set({ fltPrDrag }),
  setPrLo: (v) => {
    const prev = get();
    const key = fltKeyOf(prev);
    set((s) => {
      const f = s.fltBy[key];
      return {
        fltPrDrag: 'lo',
        fltBy: { ...s.fltBy, [key]: { ...f, pr: [Math.min(snapPrice(v), f.pr[1]), f.pr[1]] } },
      };
    });
    if (key === 'discover') get().restartDiscover(prev);
  },
  setPrHi: (v) => {
    const prev = get();
    const key = fltKeyOf(prev);
    set((s) => {
      const f = s.fltBy[key];
      return {
        fltPrDrag: 'hi',
        fltBy: { ...s.fltBy, [key]: { ...f, pr: [f.pr[0], Math.max(snapPrice(v), f.pr[0])] } },
      };
    });
    if (key === 'discover') get().restartDiscover(prev);
  },

  setStatusFilter: (k, v) => {
    const prev = get();
    const key = fltKeyOf(prev);
    set((s) => ({
      fltBy: { ...s.fltBy, [key]: { ...s.fltBy[key], st: { ...s.fltBy[key].st, [k]: v } } },
    }));
    if (key === 'discover') get().restartDiscover(prev);
  },

  toggleOwn: (k) => {
    const prev = get();
    // The store's own half of the disable. The panels grey these out, but the
    // guarantee that `Filters.own` can never hold a key whose owner we cannot
    // see has to hold for every caller, not just the two that render it —
    // `store/ownedBy.ts` has the argument.
    if (!prev.users.some((u) => u.k === k) || linkStateOf(prev, { k }) !== 'linked') return;
    const key = fltKeyOf(prev);
    const cur = prev.fltBy[key].own;
    // Rebuilt in ROSTER ORDER rather than push/splice: the chip rail reads this
    // array directly, so an insertion order would make the chips reorder
    // themselves as members are toggled off and back on.
    const next = cur.includes(k)
      ? cur.filter((x) => x !== k)
      : prev.users.filter((u) => u.k === k || cur.includes(u.k)).map((u) => u.k);
    set((s) => ({ fltBy: { ...s.fltBy, [key]: { ...s.fltBy[key], own: next } } }));
    if (next.length > 0) get().ensureOwnedBy(ownFilterAppids(get()));
    if (key === 'discover') get().restartDiscover(prev);
  },

  toggleOwnAll: () => {
    const prev = get();
    const key = fltKeyOf(prev);
    const all = ownableKeys(prev);
    // ALREADY-ALL means OFF. Comparing lengths is enough because `toggleOwn`
    // cannot put an unlinked key in the array and this action only ever writes
    // `all` — the set is a subset of `all` by construction.
    const next = prev.fltBy[key].own.length >= all.length ? [] : all;
    set((s) => ({ fltBy: { ...s.fltBy, [key]: { ...s.fltBy[key], own: next } } }));
    if (next.length > 0) get().ensureOwnedBy(ownFilterAppids(get()));
    if (key === 'discover') get().restartDiscover(prev);
  },

  tfToggle: (k) => {
    const next = tfToggleFn(get().tFilter, k, get().users.length);
    set({ tFilter: next });
    // Landing on exactly ONE member turns the board into that member's own
    // board, which needs their MY INTERESTS ordering to sort the tiers by
    // (3059). Asked HERE rather than at boot, and only for the single-member
    // case, because that is the only question the ordering answers: a member
    // who never narrows the rail to one person costs no round trips at all.
    // `ensureCrewIntOrder` is a no-op for my own key and for anyone already
    // answered, so a rail toggled back and forth asks once.
    if (next.length === 1) get().ensureCrewIntOrder(next[0] as string);
  },
  tfAll: () => set({ tFilter: [] }),

  openSheet: (kind, id) => set({ sheet: { kind, id } }),
  closeSheet: () => set({ sheet: null }),

  // -- what's new ----------------------------------------------------------
  // The queue is snapshotted on open (prototype line 1843) and NOT recomputed
  // as cards are voted: `newForMe` would drop each card the moment its vote
  // lands, so a live queue would renumber itself mid-review.
  openWhatsNew: () => {
    const s = get();
    const queue = newForMe(s.list, s.me(), (id) => s.gById(id)).map((x) => x.e.id);
    if (!queue.length) {
      // The mode moves even though nothing opened: the bell ALWAYS means news,
      // and leaving `calls` standing from a finished run would put a chore-list
      // header on the next thing that reads the view model.
      set({ wnMode: 'new' });
      s.toast('YOU ARE ALL CAUGHT UP');
      return;
    }
    set({ wnOpen: true, wnIdx: 0, wnQueue: queue, wnMode: 'new', wnSkipped: 0 });
  },

  /**
   * The Tonight nudge's CTA. It used to `nav('interests')` and leave you to
   * find the games yourself.
   *
   * `ids` comes from the strip that counted them, so its number and this run's
   * length are the same array. Recomputing when none are passed keeps the
   * action callable from anywhere — and uses the same `intQueue` the board's
   * TO-DO panel does, so a recomputed run is in the order the panel would have
   * asked for them.
   */
  openCallRun: (ids) => {
    const s = get();
    const me = s.me();
    const from = ids && ids.length ? ids : me ? intQueue(s.list, me.k, s.intSkips[me.k] ?? []) : [];
    // A game the catalog cannot resolve has no card to render.
    const queue = from.filter((id) => !!s.gById(id));
    if (!queue.length) {
      // The nudge hides itself at zero, but a stale click on a board another
      // tab has already finished is reachable.
      s.toast('EVERY GAME CALLED');
      return;
    }
    set({ wnOpen: true, wnIdx: 0, wnQueue: queue, wnMode: 'calls', wnSkipped: 0 });
  },
  closeWhatsNew: () => set({ wnOpen: false }),

  // The card advances on the optimistic write, not on the round trip: `vote`
  // and `seenUsers` both apply through `mutate`, which patches the store before
  // it awaits the commit. Awaiting here would freeze the card on a slow link.
  wnMark: async (l) => {
    const s = get();
    const id = s.wnQueue[s.wnIdx];
    if (id === undefined) return;
    const calls = s.wnMode === 'calls';
    // Mode `new`: `vote` is a toggle, but every queued entry is by definition
    // un-voted by me (`newForMe`), so the toggle IS the set the prototype
    // performed inline — and the card IS unread news, so it is marked seen.
    //
    // Mode `calls`: `voteSet`, the same writer the board's TO-DO panel uses for
    // this exact gesture. It cannot mis-toggle a game a second tab called while
    // the run was open, and it moves the id to the end of `intOrder` the way
    // every other call does. NOT marked seen: `newForMe` already drops a game
    // the moment it carries my vote, so the seen row buys nothing — and it
    // would outlive the vote. Clear the call later (the TO-DO panel is one
    // drag away) and the game would be permanently invisible to WHAT'S NEW,
    // having been suppressed by a run that never showed it as news.
    const writes = calls
      ? s.voteSet(id, l)
      : Promise.all([s.vote(id, l), s.seenUsers([id])]).then(() => undefined);
    if (s.wnIdx + 1 >= s.wnQueue.length) {
      set({ wnOpen: false });
      s.toast(calls ? runEndToast(s.wnSkipped) : 'ALL CAUGHT UP');
    } else {
      set({ wnIdx: s.wnIdx + 1 });
    }
    await writes;
  },

  /**
   * Two actions wearing one button, and keeping them apart is the point.
   *
   * Mode `new` — this card is unread news and you are dismissing it: it goes
   * into `whats_new_seen` and never comes back. Unchanged from the day it
   * shipped.
   *
   * Mode `calls` — you are deferring a CALL, not reading news. Marking it seen
   * would silently clear the WHAT'S NEW badge for a game you never opened, so
   * this writes the interests skip instead and the game comes back round at the
   * end of the queue. Same thing the board's own SKIP FOR NOW does, because it
   * is the same queue.
   */
  wnSkip: async () => {
    const s = get();
    const id = s.wnQueue[s.wnIdx];
    const calls = s.wnMode === 'calls';
    const write =
      id === undefined ? Promise.resolve() : calls ? s.intSkip(id) : s.seenUsers([id]);
    // Counted, not inferred: a deferred call is still owed, and the run has to
    // be able to say so at the end without racing its own optimistic writes.
    const skipped = calls && id !== undefined ? s.wnSkipped + 1 : s.wnSkipped;
    if (s.wnIdx + 1 >= s.wnQueue.length) {
      set({ wnOpen: false, wnSkipped: skipped });
      if (calls) s.toast(runEndToast(skipped));
    } else {
      set({ wnIdx: s.wnIdx + 1, wnSkipped: skipped });
    }
    await write;
  },

  wnClearAll: async () => {
    const s = get();
    const queue = [...s.wnQueue];
    set({ wnOpen: false });
    s.toast('ALL CLEARED');
    await s.markReviewed();
    await s.seenUsers(queue);
  },

  dragStart: (drag) => set({ drag, dragOver: null }),
  setDragOver: (dragOver) => set({ dragOver }),
  dragEnd: () => {
    const { drag, dragOver } = get();
    if (drag && dragOver && dragOver.zone === drag.zone) {
      if (drag.zone === 'goty') {
        // Two kinds of GOTY drop, keyed off the row the pointer ended on: the
        // TO-DO panel unplaces, every tier row places. `isTier` is the guard so
        // the ladder's width lives in `TIERS` alone — the hand-spelled list this
        // replaced went stale at C-F and dropped those placements on the floor.
        const t = dragOver.row;
        if (t === GOTY_TODO_ROW) void get().unplaceTier(drag.id);
        else if (isTier(t)) void get().placeTierAt(drag.id, t, dragOver.idx);
      } else {
        // Two kinds of interests drop, same shape as above: the TO-DO panel
        // clears the call, every call row sets its letter. `isCall` is the
        // guard for the reason `isTier` is — the key arrives as a bare DOM
        // string, so the row list belongs in `CALLS` and not in a cast here.
        // The `as 'y' | 'n'` this replaces asserted that anything but the
        // clear-target was a call, which was true only while there were two of
        // them (T-01).
        //
        // The panel takes `voteSet(id, '')`, not `setIntAt(id, '', idx)`: it is
        // a panel, not a row, so there is no position to drop INTO — and
        // `voteSet` is what the phone's move sheet already runs for the same
        // gesture (`? NO CALL YET`), so both routes back to uncalled behave
        // identically. `'w'` is kept as an accepted key because the move sheet
        // still spells its clear act that way.
        const v = dragOver.row;
        if (v === INT_TODO_ROW || v === 'w') void get().voteSet(drag.id, '');
        else if (isCall(v)) void get().setIntAt(drag.id, v, dragOver.idx);
      }
    }
    set({ drag: null, dragOver: null });
  },
  dragCancel: () => set({ drag: null, dragOver: null }),

  setProfileForm: (patch) => set(patch),
  // Opening or closing clears whatever was typed (the design's rule): a
  // password half-typed behind a closed sheet must not survive to a later
  // open, and re-opening must never resurrect one.
  openPw: () => set({ pwOpen: true, pfCur: '', pfN1: '', pfN2: '' }),
  closePw: () => set({ pwOpen: false, pfCur: '', pfN1: '', pfN2: '' }),
  // Opens seeded with the address in force, so the field shows what you are
  // changing FROM rather than an empty box you have to remember the answer to.
  openEm: () => set({ emOpen: true, emNew: get().pfAuthEmail, emCur: '' }),
  closeEm: () => set({ emOpen: false, emNew: '', emCur: '' }),
  // Opening re-reads the crew list: it is fetched at hydrate and never
  // invalidated, so a crew started or deleted in another tab would otherwise
  // show a stale menu until the next boot.
  openSw: () => {
    set({ swOpen: true });
    void get().loadCrews();
  },
  closeSw: () => set({ swOpen: false }),
  toggleMe: () => set({ meOpen: !get().meOpen }),
  closeMe: () => set({ meOpen: false }),
  // Both fields clear on open AND close: a half-typed crew name left behind
  // from a cancelled delete is one keystroke from being a confirmed one.
  // Refuses to re-arm while a lifecycle write is open. Without this the panel
  // is a second door to the same action: the dialog is gone during the write,
  // OWNER CONTROLS is still fully rendered for the crew being deleted, and
  // re-opening it gets you a second confirm for an operation already in flight.
  openOwn: (which) => {
    if (get().crewBusy) return;
    set({ ownModal: which, delTxt: '', trPick: '' });
  },
  closeOwn: () => set({ ownModal: '', delTxt: '', trPick: '' }),
  setOwnForm: (patch) => set(patch),
  // Opening with a custom color already worn seeds the crosshair at that
  // color's own field coordinates and arms the commit — the shell arms
  // `ccSel` on open when `me.col` exists, and the crosshair position is the
  // only coherent reading of "the swatch and hex update live". Without a
  // custom color the crosshair stays where it last was, unarmed.
  openCc: () => {
    const col = get().me()?.col;
    const ok = col ? hexToOk(col) : null;
    if (ok) {
      const { x, y } = fieldXY(ok);
      set({ ccOpen: true, ccU: x, ccV: y, ccSel: true });
    } else {
      set({ ccOpen: true, ccSel: false });
    }
  },
  closeCc: () => set({ ccOpen: false }),
  setCcPoint: (u, v) =>
    set({
      ccU: Math.min(1, Math.max(0, u)),
      ccV: Math.min(1, Math.max(0, v)),
      ccSel: true,
    }),
  setAdminForm: (patch) => set(patch),
  closeInvite: () => set({ invite: null }),

  setPtQ: (ptQ) => set({ ptQ }),
  togglePtChip: (id) =>
    set((s) => ({ ptChips: s.ptChips.includes(id) ? s.ptChips.filter((x) => x !== id) : [...s.ptChips, id] })),
  togglePtEnded: () => set((s) => ({ ptShowEnded: !s.ptShowEnded })),
  // Both ends disarm, for the same reason `openPtTracked`/`closePtTracked` do:
  // the two sheets share one `ptRmArm`, so an arm left on the tracked sheet
  // must not land the detail sheet's UNTRACK already reading `SURE?` — or the
  // reverse on the way back out.
  openPtDet: (ptDet) => set({ ptDet, ptRmArm: '' }),
  closePtDet: () => set({ ptDet: '', ptRmArm: '' }),
  openPtAdd: () =>
    set({
      ptAddOpen: true,
      ptAddTab: 'steam',
      ptAddChk: null,
      ptAddChecking: false,
      ptTracking: false,
      ptAddUrl: '',
      ptAddParty: 0,
    }),
  closePtAdd: () => set({ ptAddOpen: false }),
  setPtAddTab: (ptAddTab) => set({ ptAddTab }),
  setPtAddUrl: (ptAddUrl) => set({ ptAddUrl }),
  setPtAddParty: (ptAddParty) => set({ ptAddParty }),
  setPtOff: (patch) => set(patch),
  openPtBrowse: () => set({ ptBrowseOpen: true, ptBq: '' }),
  closePtBrowse: () => set({ ptBrowseOpen: false }),
  setPtBq: (ptBq) => set({ ptBq }),
  // Opening *and* closing disarm: a stale arm must never survive the sheet, and
  // a row-body tap leaves through `closePtTracked` on its way to the detail
  // sheet, so that path disarms too.
  openPtTracked: () => set({ ptTrackedOpen: true, ptRmArm: '' }),
  closePtTracked: () => set({ ptTrackedOpen: false, ptRmArm: '' }),
  setPtRmArm: (ptRmArm) => set({ ptRmArm }),
  openPtAsk: (ptAsk) => set({ ptAsk }),
  closePtAsk: () => set({ ptAsk: '' }),
  openPtSync: () => set({ ptSyncOpen: true }),
  closePtSync: () => set({ ptSyncOpen: false }),

  openGotyPick: ({ candidates, library, counts, ticked }) =>
    set({
      gotyPickOpen: true,
      gotyPickCands: candidates,
      gotyPickLib: library,
      gotyPickCounts: counts,
      gotyPickOn: ticked,
      // The opener resets the search, like openPtBrowse and toggleFltOpen.
      gotyPickQ: '',
    }),
  // Closing KEEPS the payload. Reopening the picker must not re-spend the round
  // trip that produced it — one keyed library call plus up to eighty achievement
  // calls — so only a fresh `steamFill` replaces it.
  closeGotyPick: () => set({ gotyPickOpen: false }),
  toggleGotyPick: (appid) =>
    set((s) => ({
      gotyPickOn: s.gotyPickOn.includes(appid)
        ? s.gotyPickOn.filter((a) => a !== appid)
        : [...s.gotyPickOn, appid],
    })),
  setGotyPickQ: (gotyPickQ) => set({ gotyPickQ }),

  bumpTick: () => set({ tick: Date.now() }),
  };
};
